import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';

import Graph from '../components/visualization/Graph';
import SearchBar from '../components/search/SearchBar';
import ArtistDetails from '../components/ArtistDetails';
import { useMusicBrainz } from '../hooks/useMusicBrainz';
import { useVisualization } from '../hooks/useVisualization';
import GrouplingsGraph from '../components/GrouplingsGraph';
import BaconMark from '../components/BaconMark';

const Visualization = () => {
  const [error, setError] = useState(null);
  const musicBrainz = useMusicBrainz();
  const visualization = useVisualization();
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const sheetDismissRef = useRef(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeArtist, setActiveArtist] = useState(null);
  const [isModalExpanded, setIsModalExpanded] = useState(false);
  const [spotifyArtistName, setSpotifyArtistName] = useState('');
  const { artistId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isArtistsView, setIsArtistsView] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [cueingName, setCueingName] = useState(location.state?.cueingName || '');
  const loadOptionsRef = useRef({ collapseSheet: true, setArtistsView: true });
  const requestSeqRef = useRef(0);
  const cueingNameRef = useRef(location.state?.cueingName || '');

  const nameForId = useCallback((id) => {
    if (!id) return '';
    const fromGraph = visualization.graphData?.nodes?.find((node) => node.id === id);
    if (fromGraph?.name) return fromGraph.name;
    const relations = activeArtist?.relations || [];
    for (const rel of relations) {
      if (rel.artist?.id === id && rel.artist.name) return rel.artist.name;
      const nested = rel.artist?.relations || [];
      for (const inner of nested) {
        if (inner.artist?.id === id && inner.artist.name) return inner.artist.name;
      }
    }
    return '';
  }, [visualization.graphData, activeArtist]);

  const queueArtist = useCallback((id, name, options) => {
    const resolved = name || nameForId(id) || '';
    cueingNameRef.current = resolved;
    setCueingName(resolved);
    setIsLoading(true);
    loadOptionsRef.current = options;
    navigate(`/artist/${id}`, { state: { cueingName: resolved } });
  }, [nameForId, navigate]);

  const applyArtist = useCallback(async (nextArtistId) => {
    if (!nextArtistId) return;

    const seq = ++requestSeqRef.current;
    const { collapseSheet, setArtistsView, expandSheet } = loadOptionsRef.current;
    loadOptionsRef.current = { collapseSheet: true, setArtistsView: true };

    setError(null);
    setIsLoading(true);
    if (cueingNameRef.current) {
      setCueingName(cueingNameRef.current);
    }

    try {
      const details = await musicBrainz.getArtistDetails(nextArtistId);
      if (seq !== requestSeqRef.current) return;
      if (!details) {
        setError('Unable to fetch artist details');
        setShowErrorModal(true);
        return;
      }

      visualization.updateGraphData(details);
      setActiveNodeId(nextArtistId);
      setActiveArtist(details);
      setSpotifyArtistName(details.name);
      setShowWelcome(false);
      if (expandSheet) setIsModalExpanded(true);
      else if (collapseSheet) setIsModalExpanded(false);
      if (setArtistsView) setIsArtistsView(true);
      cueingNameRef.current = '';
      setCueingName('');
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error('Error loading artist:', err);
      setError('An error occurred while searching. Please try again.');
      setShowErrorModal(true);
    } finally {
      if (seq === requestSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [musicBrainz, visualization]);

  const activeNodeIdRef = useRef(activeNodeId);
  activeNodeIdRef.current = activeNodeId;

  useEffect(() => {
    return musicBrainz.subscribe((artist) => {
      if (!artist?.id || artist.id !== activeNodeIdRef.current) return;
      setActiveArtist(artist);
      visualization.updateGraphData(artist);
    });
  }, [musicBrainz, visualization]);

  useEffect(() => {
    if (artistId && artistId !== activeNodeId) {
      applyArtist(artistId);
    }
    // Only the route id should start a load. applyArtist identity changes
    // would re-fetch the same artist mid-request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistId]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // Adjust this breakpoint as needed
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        if (offsetWidth > 0 && offsetHeight > 0) {
          setContainerSize({ width: offsetWidth, height: offsetHeight });
        }
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    window.addEventListener('resize', updateSize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [isMobile, isModalExpanded, activeArtist]);

  const filterImmediateRelationships = useCallback((graphData, activeId) => {
    if (!activeId) return graphData;

    const immediateNodes = new Set([activeId]);
    graphData.links.forEach(link => {
      if (link.source === activeId) immediateNodes.add(link.target);
      if (link.target === activeId) immediateNodes.add(link.source);
    });

    return {
      nodes: graphData.nodes.filter(node => immediateNodes.has(node.id)),
      links: graphData.links.filter(link => 
        immediateNodes.has(link.source) && immediateNodes.has(link.target)
      )
    };
  }, []);

  const handleSearch = useCallback(async (termOrId, name) => {
    let nextArtistId = termOrId;
    let nextName = name || '';

    if (typeof termOrId === 'string' && !termOrId.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/)) {
      nextName = termOrId;
      cueingNameRef.current = nextName;
      setCueingName(nextName);
      setIsLoading(true);
      try {
        const searchResults = await musicBrainz.searchArtist(termOrId);
        if (searchResults.length > 0) {
          nextArtistId = searchResults[0].id;
          nextName = searchResults[0].name || termOrId;
        } else {
          setError('Artist or group not found');
          setShowErrorModal(true);
          setIsLoading(false);
          cueingNameRef.current = '';
          setCueingName('');
          return;
        }
      } catch (err) {
        console.error('Error in handleSearch:', err);
        setError('An error occurred while searching. Please try again.');
        setShowErrorModal(true);
        setIsLoading(false);
        cueingNameRef.current = '';
        setCueingName('');
        return;
      }
    }

    if (nextArtistId === activeNodeId) {
      setIsLoading(false);
      cueingNameRef.current = '';
      setCueingName('');
      return;
    }

    queueArtist(nextArtistId, nextName, { expandSheet: true, setArtistsView: true });
  }, [musicBrainz, activeNodeId, queueArtist]);

  const handleNodeClick = useCallback((node) => {
    if (!node?.id) return;
    if (node.id === activeNodeId || node.isHub) {
      setIsModalExpanded(true);
      return;
    }
    queueArtist(node.id, node.name, { expandSheet: true, setArtistsView: true });
  }, [activeNodeId, queueArtist]);

  const handleMemberClick = useCallback((memberId, switchToArtistsView = true, name) => {
    if (!memberId || memberId === activeNodeId) return;
    queueArtist(memberId, name, { collapseSheet: false, setArtistsView: switchToArtistsView });
  }, [activeNodeId, queueArtist]);

  const toggleModalExpansion = () => {
    setIsModalExpanded(!isModalExpanded);
  };

  const handlePagePointerDown = (event) => {
    if (!isModalExpanded) return;
    if (event.target.closest('.artist-details-sheet, .artist-details-modal, .sheet-dock, .sheet-pull, .visualization-header, .error-modal-overlay')) {
      sheetDismissRef.current = null;
      return;
    }
    sheetDismissRef.current = { x: event.clientX, y: event.clientY };
  };

  const handlePagePointerUp = (event) => {
    if (!isModalExpanded || !sheetDismissRef.current) return;
    const start = sheetDismissRef.current;
    sheetDismissRef.current = null;
    if (event.target.closest('.artist-details-sheet, .artist-details-modal, .sheet-dock, .sheet-pull, .visualization-header, .error-modal-overlay')) {
      return;
    }
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
    setIsModalExpanded(false);
  };

  const filteredGraphData = useMemo(() => {
    const data = filterImmediateRelationships(visualization.graphData, activeNodeId);
    
    const sortedMembers = Object.values(data.nodes)
      .filter(node => node.id !== activeNodeId)
      .sort((a, b) => {
        // Sorting logic remains the same as in ArtistDetails
        const rolePriority = (member) => {
          const roles = member.roles || [];
          if (roles.some(role => role.toLowerCase().includes('lead vocals'))) return 1;
          if (roles.some(role => role.toLowerCase().includes('vocals'))) return 2;
          if (roles.some(role => role.toLowerCase().includes('guitar'))) return 3;
          return 4;
        };

        const priorityA = rolePriority(a);
        const priorityB = rolePriority(b);

        if (a.isOriginal !== b.isOriginal) {
          return a.isOriginal ? -1 : 1;
        }

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        return 0; // If all else is equal, maintain original order
      });

    const nodesWithImportance = data.nodes.map((node) => {
      const sortIndex = node.id === activeNodeId ? -1 : sortedMembers.findIndex(member => member.id === node.id);
      return {
        ...node,
        value: sortIndex === -1 ? sortedMembers.length + 1 : sortIndex + 1, // +1 to avoid zero-based index
      };
    });

    return {
      nodes: nodesWithImportance,
      links: data.links
    };
  }, [filterImmediateRelationships, visualization.graphData, activeNodeId]);

  const prepareGroupsData = useCallback(() => {
    if (!activeArtist) return { relatedGroups: [] };

    const members = (activeArtist.relations || []).filter(rel => rel.type === 'member of band');
    const groupsWithMembers = members.reduce((acc, member) => {
      if (member.artist && member.artist.relations) {
        member.artist.relations
          .filter(rel => rel.type === 'member of band' && rel.artist && rel.artist.id !== activeArtist.id)
          .forEach(groupRel => {
            if (groupRel.artist && groupRel.artist.name) {
              if (!acc[groupRel.artist.id]) {
                acc[groupRel.artist.id] = {
                  id: groupRel.artist.id,
                  name: groupRel.artist.name,
                  members: new Map()
                };
              }
              acc[groupRel.artist.id].members.set(member.artist.id, member.artist.name);
            }
          });
      }
      return acc;
    }, {});

    const sortedGroups = Object.values(groupsWithMembers)
      .map(group => ({
        ...group,
        count: group.members.size,
        members: Array.from(group.members, ([id, name]) => ({ id, name }))
      }))
      .sort((a, b) => b.count - a.count);

    const maxCount = sortedGroups[0]?.count || 1;
    return {
      relatedGroups: [
        {
          id: activeArtist.id,
          name: activeArtist.name,
          count: maxCount,
          isHub: true,
          members: [],
        },
        ...sortedGroups,
      ],
    };
  }, [activeArtist]);

  return (
    <div
      className={`visualization-page ${isModalExpanded ? 'sheet-open' : ''}`}
      onPointerDown={handlePagePointerDown}
      onPointerUp={handlePagePointerUp}
    >
      <div className="visualization-header">
        <div className="active-artist-header">
          <Link
            to="/profile"
            state={{ from: `${location.pathname}${location.search}` }}
            className="profile-mark"
            aria-label="Profile"
          >
            <BaconMark />
          </Link>
          {activeArtist ? null : error ? (
            <span className="active-artist-name not-found">Artist Not Found</span>
          ) : null}
          <div className="header-tools">
            <SearchBar onSearch={handleSearch} activeArtist={activeArtist} />
          </div>
        </div>
      </div>

      {error && showErrorModal && (
        <div className="error-modal-overlay">
          <div className="error-modal">
            <p>{error}</p>
            <button onClick={() => setShowErrorModal(false)}>OK</button>
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className={`visualization-stage ${isLoading ? 'is-loading' : ''}`}
      >
        {!showWelcome && containerSize.width > 0 && containerSize.height > 0 && (
          isArtistsView ? (
            <Graph
              data={filteredGraphData}
              onNodeClick={handleNodeClick}
              width={containerSize.width}
              height={containerSize.height}
              isMobile={isMobile}
              activeNodeId={activeNodeId}
            />
          ) : (
            <div className="grouplings-container">
              <GrouplingsGraph
                data={prepareGroupsData()}
                onNodeClick={handleNodeClick}
                isMobile={isMobile}
              />
            </div>
          )
        )}
        {isLoading && !activeArtist && (
          <div className="stage-cue">
            <p>
              {cueingName || 'Loading'}
              <span className="cue-dots" aria-hidden="true"></span>
            </p>
          </div>
        )}
      </div>
      {activeArtist && (
        <ArtistDetails
          artist={activeArtist}
          spotifyArtistName={spotifyArtistName}
          isExpanded={isModalExpanded}
          onToggleExpand={toggleModalExpansion}
          isMobile={isMobile}
          onMemberClick={handleMemberClick}
          isArtistsView={isArtistsView}
          onToggleView={setIsArtistsView}
          isCueing={isLoading}
          cueingName={cueingName}
          cueingId={isLoading ? artistId : ''}
        />
      )}
    </div>
  );
};

export default Visualization;
